import React, { memo } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardIcon from '@mui/icons-material/KeyboardAlt';
import MicIcon from '@mui/icons-material/Mic';
import { ButtonContainer, ActionButton } from './DynamicForm.styles.index';

// Production constants
const CONSTANTS = {
  BUTTON_LABELS: {
    MANUAL_ENTRY: 'Manual Entry',
    SPEECH_ENTRY: 'Voice Entry',
    CANCEL: 'Cancel',
  },
  ARIA_LABELS: {
    MANUAL_BUTTON: 'Start manual entry',
    SPEECH_BUTTON: 'Start voice entry',
    CANCEL_BUTTON: 'Cancel workflow',
  },
} as const;

interface EntryButtonsProps {
  onManualEntry: () => void;
  onSpeechEntry: () => void;
  onCancel: () => void;
  showManualButton?: boolean;
  showSpeechOption?: boolean;
  showCancelButton?: boolean;
}

const isCallable = (fn: unknown): fn is () => void => !!fn && typeof fn === 'function';

const getButtonVisibility = (props: {
  showManualButton?: boolean;
  showSpeechOption?: boolean;
  showCancelButton?: boolean;
  onManualEntry: () => void;
  onSpeechEntry: () => void;
  onCancel: () => void;
}) => ({
  manual: !!props.showManualButton && isCallable(props.onManualEntry),
  speech: !!props.showSpeechOption && isCallable(props.onSpeechEntry),
  cancel: !!props.showCancelButton && isCallable(props.onCancel),
});

export const EntryButtons: React.FC<EntryButtonsProps> = memo(({
  onManualEntry,
  onSpeechEntry,
  onCancel,
  showManualButton = true,
  showSpeechOption = true,
  showCancelButton = true,
}) => {
  const visibility = getButtonVisibility({
    showManualButton,
    showSpeechOption,
    showCancelButton,
    onManualEntry,
    onSpeechEntry,
    onCancel,
  });

  if (!visibility.manual && !visibility.speech && !visibility.cancel) {
    return null;
  }

  return (
    <ButtonContainer role="group" aria-label="Workflow options">
      {visibility.manual && (
        <ActionButton
          onClick={onManualEntry}
          variant='primary'
          icon={KeyboardIcon}
          aria-label={CONSTANTS.ARIA_LABELS.MANUAL_BUTTON}
        >
          {CONSTANTS.BUTTON_LABELS.MANUAL_ENTRY}
        </ActionButton>
      )}

      {visibility.speech && (
        <ActionButton
          onClick={onSpeechEntry}
          variant='primary'
          icon={MicIcon}
          aria-label={CONSTANTS.ARIA_LABELS.SPEECH_BUTTON}
        >
          {CONSTANTS.BUTTON_LABELS.SPEECH_ENTRY}
        </ActionButton>
      )}

      {visibility.cancel && (
        <ActionButton
          onClick={onCancel}
          variant='primary'
          icon={CloseIcon}
          aria-label={CONSTANTS.ARIA_LABELS.CANCEL_BUTTON}
        >
          {CONSTANTS.BUTTON_LABELS.CANCEL}
        </ActionButton>
      )}
    </ButtonContainer>
  );
});

EntryButtons.displayName = 'EntryButtons';

